<template>
  <div class="modal is-active">
    <div class="modal-background" @click="$emit('close')"></div>
    <div class="modal-content">
      <div class="box modal-container">
        <div class="pa-modal pa-cancel">
          <!-- design pa-app.jsx cancelReq: brand icon circle, muted body, .pa-radio options, danger outline confirm -->
          <div class="pa-cancel__icon" aria-hidden="true"><i class="material-icons">cancel</i></div>
          <div class="pa-modal__title">Cancel your {{ packageName }} subscription?</div>
          <div class="pa-modal__body">
            <p v-if="unpaid" class="pa-cancel__intro">
              Your first invoice is not paid yet, so nothing has been charged. We withdraw the invoice and close the
              project. Your audit and content plans stay here, and you can restart with any package whenever you are
              ready.
            </p>
            <p v-else class="pa-cancel__intro">
              Your campaign continues until the end of the current month{{ endLabel ? ' (' + endLabel + ')' : '' }}.
              Everything already in production is delivered and you are not charged again. There is no refund for the
              current month.
            </p>
            <div class="pa-cancel__reasons">
              <div class="pa-cancel__label">Help us improve: why are you cancelling?</div>
              <label
                v-for="r in reasons"
                :key="r.key"
                class="pa-cancel__reason"
                :class="{ 'is-selected': reason === r.key }"
              >
                <input type="radio" class="pa-cancel__input" name="pa-cancel-reason" :value="r.key" v-model="reason" />
                <span class="pa-cancel__rb" aria-hidden="true"></span>
                <span class="pa-cancel__rl">{{ r.label }}</span>
              </label>
              <textarea
                v-model="feedback"
                class="pa-in pa-cancel__feedback"
                rows="3"
                maxlength="4000"
                placeholder="Anything else you want your SEO Manager to know? (optional)"
              ></textarea>
            </div>
          </div>
          <div class="pa-modal__actions">
            <button type="button" class="pa-btn pa-btn--out" @click="$emit('close')">Keep my package</button>
            <button
              type="button"
              class="pa-btn pa-btn--out pa-cancel__confirm"
              :disabled="busy"
              @click="$emit('confirm', { reason, feedback })"
            >Cancel subscription</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { POP_AUTHORITY_CANCEL_REASONS } from '@/utils/popAuthority'

function parseApiDate (value) {
  if (!value) return null
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}):(\d{2}))?/.exec(String(value))
  if (!m) return null
  return new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], +(m[4] || 0), +(m[5] || 0), +(m[6] || 0)))
}

/**
 * Cancel survey (design pa-app.jsx cancelReq). Emits `confirm` with {reason, feedback}. A live package
 * ends with the current month; with `unpaid` (first invoice not paid) the invoice is withdrawn right away.
 */
export default {
  name: 'PopAuthorityCancelSubscriptionModal',
  props: {
    subscription: { type: Object, required: true },
    unpaid: { type: Boolean, default: false },
    busy: { type: Boolean, default: false },
  },
  data () {
    return {
      reason: 'other',
      feedback: '',
      reasons: POP_AUTHORITY_CANCEL_REASONS,
    }
  },
  computed: {
    packageName () {
      return (this.subscription.package && this.subscription.package.name) || 'package'
    },
    endLabel () {
      const d = parseApiDate(this.subscription.currentPeriodEnd)
      return d ? 'ends ' + d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''
    },
  },
}
</script>

<style lang="scss" scoped>
@import './setup/setup.scss';

.pa-cancel {
  /* design .pa-modal .mic.brand: 46px accent-soft circle with the accent icon */
  &__icon {
    width: 46px;
    height: 46px;
    border-radius: 50%;
    background: $pa-accent-soft;
    color: $pa-accent;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 12px;

    .material-icons {
      font-size: 24px;
      color: inherit;
    }
  }

  /* design .pa-modal p: muted body copy */
  &__intro {
    margin: 0;
    color: $pa-muted;
  }

  &__reasons {
    margin-top: 14px;
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  &__label {
    font-weight: 700;
    font-size: 0.9rem;
    color: $pa-teal;
    margin-bottom: 4px;
  }

  /* design pa.css .pa-radio: 18px circle, 2px border, accent ring + dot when selected, 600 label */
  &__reason {
    display: flex;
    gap: 10px;
    align-items: flex-start;
    padding: 6px 0;
    font-size: 0.9rem;
    cursor: pointer;
    position: relative;
  }

  &__input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
    margin: 0;
  }

  &__rb {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    border: 2px solid $pa-border;
    flex: none;
    margin-top: 1px;
    position: relative;
    background: #fff;
  }

  &__input:focus-visible + &__rb {
    box-shadow: 0 0 0 3px rgba(255, 87, 0, 0.25);
  }

  &__reason.is-selected &__rb {
    border-color: $pa-accent;

    &::after {
      content: '';
      position: absolute;
      inset: 3px;
      border-radius: 50%;
      background: $pa-accent;
    }
  }

  &__rl {
    font-weight: 600;
    color: $pa-teal;
    line-height: 1.45;
  }

  /* design: outline button in the danger colour */
  &__confirm {
    color: $pa-danger;
    border-color: $pa-danger;

    &:hover:not(:disabled) {
      background: #fbe8e8;
    }
  }

  &__feedback {
    margin-top: 6px;
    resize: vertical;
    min-height: 70px;
  }
}
</style>
